import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { ShieldCheck, FileText, ExternalLink } from "lucide-react";
import { toast } from "sonner";

interface KycRow {
  id: string;
  user_id: string;
  full_name: string;
  date_of_birth: string;
  address_line1: string;
  address_city: string;
  address_region: string;
  address_postal: string;
  address_country: string;
  id_type: string;
  id_last4: string;
  status: string;
  admin_notes: string | null;
  document_urls: string[] | null;
  created_at: string;
}

const AdminKyc = () => {
  const [rows, setRows] = useState<KycRow[]>([]);
  const [filter, setFilter] = useState<"pending" | "approved" | "rejected" | "all">("pending");
  const [busy, setBusy] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});

  const load = async () => {
    let q = supabase.from("kyc_submissions").select("*").order("created_at", { ascending: false });
    if (filter !== "all") q = q.eq("status", filter);
    const { data, error } = await q.limit(100);
    if (error) toast.error(error.message);
    setRows((data ?? []) as KycRow[]);
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const openDoc = async (path: string) => {
    const { data, error } = await supabase.storage
      .from("kyc-docs")
      .createSignedUrl(path, 300);
    if (error || !data?.signedUrl) {
      toast.error(error?.message ?? "Could not open document");
      return;
    }
    window.open(data.signedUrl, "_blank", "noopener");
  };

  const review = async (id: string, decision: "approved" | "rejected") => {
    setBusy(id);
    const { error } = await supabase.rpc("admin_review_kyc", {
      _submission_id: id,
      _decision: decision,
      _notes: notes[id] || null,
    });
    setBusy(null);
    if (error) toast.error(error.message);
    else {
      toast.success(`Marked ${decision}`);
      load();
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" />
            Identity verification
          </h1>
          <p className="text-sm text-muted-foreground">Review user-submitted KYC.</p>
        </div>
        <div className="flex gap-1">
          {(["pending", "approved", "rejected", "all"] as const).map((s) => (
            <Button
              key={s}
              size="sm"
              variant={filter === s ? "default" : "outline"}
              onClick={() => setFilter(s)}
            >
              {s}
            </Button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        {rows.length === 0 && (
          <Card className="p-4 text-sm text-muted-foreground">No submissions.</Card>
        )}
        {rows.map((r) => (
          <Card key={r.id} className="p-3 space-y-2">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-medium">{r.full_name}</div>
                <div className="text-xs text-muted-foreground">
                  DOB {r.date_of_birth} · {r.id_type} ****{r.id_last4}
                </div>
                <div className="text-xs text-muted-foreground">
                  {r.address_line1}, {r.address_city}, {r.address_region} {r.address_postal} {r.address_country}
                </div>
                <div className="text-[10px] text-muted-foreground font-mono mt-1">
                  user {r.user_id}
                </div>
              </div>
              <Badge
                variant={
                  r.status === "approved"
                    ? "default"
                    : r.status === "rejected"
                    ? "destructive"
                    : "secondary"
                }
              >
                {r.status}
              </Badge>
            </div>

            {r.document_urls && r.document_urls.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {r.document_urls.map((p) => {
                  const name = p.split("/").pop() ?? p;
                  return (
                    <button
                      key={p}
                      type="button"
                      onClick={() => openDoc(p)}
                      className="text-xs inline-flex items-center gap-1 rounded-full bg-muted hover:bg-muted/70 px-2.5 py-1 transition-smooth"
                    >
                      <FileText className="h-3 w-3" />
                      {name}
                      <ExternalLink className="h-3 w-3" />
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="text-xs text-muted-foreground italic">No documents uploaded</div>
            )}

            {r.admin_notes && (
              <div className="text-xs text-muted-foreground">
                <span className="font-semibold">Notes:</span> {r.admin_notes}
              </div>
            )}

            {r.status === "pending" && (
              <div className="space-y-2">
                <Textarea
                  placeholder="Notes (shown to user if rejected)"
                  rows={2}
                  value={notes[r.id] ?? ""}
                  onChange={(e) => setNotes((n) => ({ ...n, [r.id]: e.target.value }))}
                />
                <div className="flex gap-2 justify-end">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy === r.id}
                    onClick={() => review(r.id, "rejected")}
                  >
                    Reject
                  </Button>
                  <Button
                    size="sm"
                    disabled={busy === r.id}
                    onClick={() => review(r.id, "approved")}
                  >
                    Approve
                  </Button>
                </div>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminKyc;
